import React, { useState } from 'react';
import { ArrowDown } from 'lucide-react';
import styled from '../../styled';

/** Logos live in website/static/images/data-destinations/. */
const DESTINATIONS = [
  { name: 'BigQuery', logo: '/images/data-destinations/bigquery.svg' },
  { name: 'Snowflake', logo: '/images/data-destinations/snowflake.svg' },
  { name: 'Redshift', logo: '/images/data-destinations/redshift.svg' },
  { name: 'Power BI', logo: '/images/data-destinations/power-bi.svg' },
  { name: 'Looker', logo: '/images/data-destinations/looker.svg' },
  { name: 'Tableau', logo: '/images/data-destinations/tableau.svg' },
  { name: 'Databricks', logo: '/images/data-destinations/databricks.svg' },
  { name: 'Metabase', logo: '/images/data-destinations/metabase.svg' }
] as const;

const Panel = styled('div')`
  width: 100%;
  padding: 22px 22px 20px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 12px 40px rgba(14, 28, 43, 0.08);
`;

const Flow = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const Hub = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  color: #0e1c2b;
  font-size: 12px;
  font-weight: 700;

  img {
    width: 22px;
    height: 22px;
    object-fit: contain;
  }
`;

const FlowArrow = styled(ArrowDown)`
  color: #94a3b8;
`;

const GridIntro = styled('div')`
  margin-bottom: 14px;
  color: #64748b;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-align: center;
  text-transform: uppercase;
`;

const LogoGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 560px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const LogoTile = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 68px;
  padding: 10px 8px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: #d4e0ff;
    box-shadow: 0 4px 14px rgba(46, 92, 255, 0.08);
  }
`;

const LogoMark = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 24px;

  img {
    max-width: 100%;
    max-height: 22px;
    object-fit: contain;
  }
`;

const LogoLabel = styled('span')`
  color: #64748b;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-align: center;
  line-height: 1.2;
`;

const LogoFallback = styled('span')`
  color: #64748b;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.01em;
  text-align: center;
  line-height: 1.2;
`;

const DestinationLogo = ({ name, logo }: { name: string; logo: string }) => {
  const [failed, setFailed] = useState(false);

  return (
    <LogoTile>
      <LogoMark>
        {!failed ? (
          <img src={logo} alt="" onError={() => setFailed(true)} />
        ) : (
          <LogoFallback>{name}</LogoFallback>
        )}
      </LogoMark>
      {!failed && <LogoLabel>{name}</LogoLabel>}
    </LogoTile>
  );
};

export const WarehouseIntegrationsMockup = () => (
  <Panel aria-label="Warehouse and BI destination integrations">
    <Flow aria-hidden>
      <Hub>
        <img src="/favicon.png" alt="" />
        Affilimate Unify
      </Hub>
      <FlowArrow size={16} />
    </Flow>
    <GridIntro>merges cleanly into your in-house data stack</GridIntro>
    <LogoGrid>
      {DESTINATIONS.map((destination) => (
        <DestinationLogo
          key={destination.name}
          name={destination.name}
          logo={destination.logo}
        />
      ))}
    </LogoGrid>
  </Panel>
);
